import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface ReportScheduleConfig {
  schedule: string;
  time: string;
  report_type: string;
  recipient: string;
  email_notifications: boolean;
}

const WORKFLOW_NAME = "Scheduled Reports";

const DEFAULTS: ReportScheduleConfig = {
  schedule: "weekly",
  time: "08:00",
  report_type: "analytics",
  recipient: "",
  email_notifications: true,
};

/**
 * Report email schedules are stored on the user's "Scheduled Reports" workflow
 * (workflows.config.reports) — the same shape the process-scheduled-tasks edge
 * function reads. No separate table, so there is a single source of truth.
 */
export const useReportSchedule = () => {
  const [config, setConfig] = useState<ReportScheduleConfig>(DEFAULTS);
  const [workflowId, setWorkflowId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from("workflows")
        .select("id, config, status")
        .eq("created_by", user.id)
        .eq("name", WORKFLOW_NAME)
        .maybeSingle();

      if (data) {
        setWorkflowId(data.id);
        const reports = (data.config as any)?.reports ?? {};
        setConfig({
          ...DEFAULTS,
          ...reports,
          recipient: reports.recipient || user.email || "",
        });
      } else {
        setConfig({ ...DEFAULTS, recipient: user.email || "" });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (next: ReportScheduleConfig) => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("You must be signed in to save a schedule.");

      const payload = {
        name: WORKFLOW_NAME,
        created_by: user.id,
        status: "active",
        schedule: next.schedule,
        config: { reports: { ...next } } as unknown as Record<string, unknown>,
      } as never;

      if (workflowId) {
        const { error } = await supabase
          .from("workflows")
          .update(payload)
          .eq("id", workflowId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from("workflows")
          .insert(payload)
          .select("id")
          .single();
        if (error) throw error;
        setWorkflowId(data.id);
      }

      setConfig(next);
      return true;
    } finally {
      setSaving(false);
    }
  }, [workflowId]);

  const disable = useCallback(async () => {
    if (!workflowId) return;
    setSaving(true);
    try {
      await supabase.from("workflows").update({ status: "paused" }).eq("id", workflowId);
    } finally {
      setSaving(false);
    }
  }, [workflowId]);

  return { config, setConfig, loading, saving, save, disable, isScheduled: !!workflowId };
};
